import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, capitalize } from "@mui/material"
import type { FC, ReactElement } from "react"
import React, { useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { DOCUMENT_UPLOAD_MIME_TYPES } from "@/components/upload/constants"
import useHandleFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import type { CompanyRegistration, CompanyRegistrationType } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import usePrev from "@/hooks/usePrev"
import RegistrationForm from "@/screens/CompanySettings/Registration/forms/RegistrationForm"
import type { RegistrationFormValues } from "@/screens/CompanySettings/Registration/forms/registrationSchema"
import { RegistrationSchema } from "@/screens/CompanySettings/Registration/forms/registrationSchema"
import useRegistrationDialogActions from "@/screens/CompanySettings/Registration/hooks/useRegistrationDialogActions"
import { buildDefaultFormValues } from "@/screens/CompanySettings/Registration/utils"

interface RegistrationFormDialogProps {
  companyId: string
  onClose: () => void
  onSuccess?: () => void
  presetRegistrationType?: CompanyRegistrationType
  data?: CompanyRegistration
}

const defaultFormValues: Omit<RegistrationFormValues, "companyId"> = {
  locationId: "",
  registrationType: null,
  registrationNumber: "",
  registrationDocument: undefined,
  taxExemptionDocument: undefined,
}

const RegistrationFormDialog: FC<RegistrationFormDialogProps> = ({
  companyId,
  presetRegistrationType,
  data,
  onClose,
  onSuccess,
}): ReactElement => {
  const isCreateMode = !data?.companyRegistrationId

  const methods = useForm<RegistrationFormValues>({
    defaultValues: isCreateMode
      ? {
          ...defaultFormValues,
          registrationType: presetRegistrationType ?? defaultFormValues.registrationType,
          companyId,
        }
      : buildDefaultFormValues(data as CompanyRegistration),
    resolver: zodResolver(RegistrationSchema),
    mode: "onChange",
  })
  const registrationTypeValue = methods.watch("registrationType")
  const prevRegistrationTypeValue = usePrev(registrationTypeValue)

  const { uploadFile, onMarkFileAsNeedRemoval, removeFiles } = useHandleFileUpload({
    referenceTableName: ReferenceTableName.CompanyRegistration,
    allowedFileTypes: DOCUMENT_UPLOAD_MIME_TYPES,
  })

  const { setIsCancelConfirmationOpen, isCancelConfirmationOpen, canSubmit, handleSubmit, handleCancel } =
    useRegistrationDialogActions({
      methods,
      onSuccess: async (data: CompanyRegistration) => {
        const registrationDocument = methods.getValues("registrationDocument")
        const taxExemptionDocument = methods.getValues("taxExemptionDocument")

        if (registrationDocument) {
          await uploadFile({
            file: registrationDocument,
            referenceId: data.companyRegistrationId!,
            type: FieldGroupEnum.CompanyRegistrationNumber,
          })
        }

        if (taxExemptionDocument) {
          await uploadFile({
            file: taxExemptionDocument,
            referenceId: data.companyRegistrationId!,
            type: FieldGroupEnum.TaxExempt,
          })
        }
        removeFiles()

        onSuccess?.()
      },
      onClose,
    })

  // Reset dependable fields when registrationType changes
  useEffect(() => {
    if (!prevRegistrationTypeValue || registrationTypeValue === prevRegistrationTypeValue) {
      return
    }
    methods.reset({
      ...methods.getValues(),
      registrationNumber: defaultFormValues.registrationNumber,
      taxExemptionNumber: defaultFormValues.taxExemptionNumber,
      registrationDocument: undefined,
      taxExemptionDocument: undefined,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationTypeValue, prevRegistrationTypeValue])

  const dialogTitle = isCreateMode
    ? "Add Registration"
    : `Edit  ${capitalize(registrationTypeValue?.toLowerCase() ?? "")} Registration`

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      )}
      <Dialog
        open={true}
        onClose={handleCancel}
        maxWidth='sm'
        fullWidth
        data-testid='registration-dialog'
        sx={{ display: isCancelConfirmationOpen ? "none" : "block" }}
      >
        <Box>
          <DialogHeader title={dialogTitle} onClose={handleCancel} />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <RegistrationForm
                onSubmit={handleSubmit}
                onMarkFileAsNeedRemoval={onMarkFileAsNeedRemoval}
                presetRegistrationType={presetRegistrationType}
              />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              type='submit'
              disabled={!canSubmit}
              onClick={handleSubmit}
              data-testid='submit-button'
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  ) as ReactElement
}

export default RegistrationFormDialog
